import "./smallModal.css";
import { useState, useEffect } from "react";
import Input from "../../fluxo-engenheiro/formBasic/Input";
import { ModalButtons } from "../../ModalButtons/modalButtons";
import { getUserAutocomplete } from "../../../redux/actions/fluxoNSimilar";
import { AppDispatch } from "recharts/types/state/store";
import { useDispatch } from "react-redux";
import InputCheckBoxEngenheiros from "../../inputCheckBox/inputCheckBoxEngenheiros";
import { OptionsEngenheiro } from "../../../utils/optionsAutocomplete";
import InfoAlert from "../../alertComponent/alertUpdated";

export interface EditFormDataTax {
  type: "tax";
  email: string;
  crea: string;
}

export interface EditFormDataEng {
  type: "eng";
  engineersToBecomeAssociates: string[];
}

export type EditFormData = EditFormDataTax | EditFormDataEng;

interface SmallModalEditProps {
  titleModal?: string;
  mode: "create" | "edit";
  initialData?: EditFormData;
  onConfirm: (data: EditFormData) => void;
  onClose: () => void;
  isSubmitting?: boolean;
  isButtonDisabled?: boolean;
  onDisableButton?: (disabled: boolean) => void;
  className?: string;
  titleDescription?: React.ReactNode;
  bodyModal?: React.ReactNode;
  buttonOne?: string;
  titleLabel?: string;
  buttonTwo?: string;
  buttonThree?: string;
  onLogout?: () => void;
  isAssociated?: boolean;
}

export const SmallModalEdit = ({
  titleModal,
  titleDescription,
  bodyModal,
  buttonOne = "Confirmar",
  buttonTwo = "Cancelar",
  buttonThree,
  mode,
  initialData,
  onClose,
  onConfirm,
  onLogout,
  className,
  isButtonDisabled,
  onDisableButton,
  isSubmitting,
  isAssociated = false,
}: SmallModalEditProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const getEmptyForm = (isAssociated?: boolean): EditFormData => {
    if (isAssociated) {
      return {
        type: "eng",
        engineersToBecomeAssociates: [],
      };
    }
    return {
      type: "tax",
      email: "",
      crea: "",
    };
  };

  const [form, setForm] = useState<EditFormData>(getEmptyForm(isAssociated));

  const [emailSearch, setEmailSearch] = useState("");
  const [emailDropdown, setEmailDropdown] = useState<any[]>([]);
  const [showEmailDropdown, setShowEmailDropdown] = useState(false);
  const [engenheiroInput, setEngenheiroInput] = useState("");
  const [engenheiroOptions, setEngenheiroOptions] = useState<
    OptionsEngenheiro[]
  >([]);
  const [selectedEngenheiros, setSelectedEngenheiros] = useState<
    OptionsEngenheiro[]
  >([]);

  /** INIT MODE */
  useEffect(() => {
    if (mode === "edit" && initialData) {
      setForm(initialData);

      if (initialData.type === "tax") {
        setEmailSearch(initialData.email);
      } else if (initialData.type === "eng") {
        const mappedEng = initialData.engineersToBecomeAssociates.map(
          (email) => ({
            label: email,
            value: email,
          }),
        );
        setSelectedEngenheiros(mappedEng);
      }
    }

    if (mode === "create") {
      const emptyForm = getEmptyForm(isAssociated);
      setForm(emptyForm);
      setEmailSearch("");
      setSelectedEngenheiros([]);
      setEngenheiroInput("");
    }
  }, [mode, initialData, isAssociated]);

  /** DISABLE BUTTON */
  useEffect(() => {
    if (onDisableButton) {
      let isInvalid = false;
      if (form.type === "tax") {
        isInvalid = !form.email.trim() || !form.crea.trim();
      } else if (form.type === "eng") {
        isInvalid = form.engineersToBecomeAssociates.length === 0;
      }
      onDisableButton(isInvalid);
    }
  }, [form, onDisableButton]);

  /** EMAIL SEARCH */
  const handleEmailChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmailSearch(value);

    if (form.type === "tax") {
      setForm((prev) => ({ ...prev, email: value }));
    }

    if (value.length < 3) {
      setEmailDropdown([]);
      setShowEmailDropdown(false);
      return;
    }

    try {
      const res = await dispatch(
        getUserAutocomplete({ search: value }),
      ).unwrap();
      if (res.length === 0) {
        setShowEmailDropdown(false);
      } else {
        setEmailDropdown(res);
        setShowEmailDropdown(true);
      }
    } catch {
      setShowEmailDropdown(false);
    }
  };

  const handleSelectEmail = (email: string) => {
    if (form.type === "tax") {
      setForm((prev) => ({ ...prev, email }));
      setEmailSearch(email);
      setShowEmailDropdown(false);
    }
  };

  /** ENGENHEIROS SEARCH */
  const handleSearchEngenheiros = async (value: string) => {
    if (value.length < 3) {
      setEngenheiroOptions([]);
      return;
    }

    const res = await dispatch(getUserAutocomplete({ search: value })).unwrap();

    setEngenheiroOptions(
      res.map((item: any) => ({
        label: item.email,
        value: item.email,
      })),
    );
  };

  const handleEngenheirosChange = (
    _: React.SyntheticEvent,
    newValue: OptionsEngenheiro[],
  ) => {
    setSelectedEngenheiros(newValue);

    if (form.type === "eng") {
      setForm((prev) => ({
        ...prev,
        engineersToBecomeAssociates: newValue.map((o) => o.value),
      }));
    }
  };

  const clearEngenheiros = () => {
    setSelectedEngenheiros([]);
    setEngenheiroOptions([]);
    setEngenheiroInput("");

    if (form.type === "eng") {
      setForm((prev) => ({
        ...prev,
        engineersToBecomeAssociates: [],
      }));
    }
  };

  return (
    <div
      className={`smallModalCorporateSession ${className}`}
      data-testid="edit-modal"
    >
      <div className="smallModal">
        <div className="containerTitleSmallModal">
          {titleModal && <h1 className="titleSmallModal">{titleModal}</h1>}
        </div>

        <div className="containerDescription">
          {titleDescription && (
            <span className="bodySmallModal">{titleDescription}</span>
          )}
          {bodyModal && <span className="bodySmallModal">{bodyModal}</span>}

          {form.type === "tax" ? (
            <>
              <span style={{ marginTop: 15 }}>Email:</span>
              <div style={{ position: "relative" }}>
                <Input
                  label=""
                  width="100%"
                  placeholder="Email"
                  value={emailSearch}
                  onChange={handleEmailChange}
                  disabled={mode === "edit"}
                  data-testid="input-email" // ✅ Teste friendly
                />

                {showEmailDropdown && emailDropdown.length > 0 && (
                  <div
                    className="dataResult aprovadorDropdown"
                    data-testid="email-dropdown"
                  >
                    {emailDropdown.map((item: any, index) => (
                      <button
                        key={item.email || index}
                        onClick={() => handleSelectEmail(item.email)}
                        onMouseDown={(e) => e.preventDefault()}
                        data-testid={`email-option-${item.email}`} // ✅ Teste friendly
                        className="dataItem"
                        aria-label={`Selecionar email ${item.email}`}
                      >
                        {item.email}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <span style={{ marginTop: 15 }}>Número do CREA:</span>
              <Input
                label=""
                width="100%"
                placeholder="Número do CREA"
                value={form.crea}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, crea: e.target.value }))
                }
                data-testid="input-crea" // ✅ Teste friendly
              />
            </>
          ) : (
            <>
              <InfoAlert
                text="Ao associar usuários ao meu perfil, declaro-me responsável pelos laudos técnicos por eles criados e permito que 
      meu CREA conste como responsável técnico."
              />
              <span style={{ marginTop: 15 }}>Usuários associados:</span>
              <InputCheckBoxEngenheiros
                value={selectedEngenheiros}
                onChange={handleEngenheirosChange}
                options={engenheiroOptions}
                placeholder="Engenheiros associados"
                inputValue={engenheiroInput}
                setInputValue={setEngenheiroInput}
                onSearchEngenheiros={handleSearchEngenheiros}
                clearState={clearEngenheiros}
                data-testid="input-engenheiros" // ✅ Teste friendly
              />
            </>
          )}
        </div>

        <ModalButtons
          buttonOne={buttonOne}
          buttonTwo={buttonTwo}
          buttonThree={buttonThree}
          onConfirm={() => onConfirm(form)}
          onClose={onClose}
          onLogout={onLogout}
          isButtonDisabled={isButtonDisabled}
          isSubmitting={isSubmitting}
        />
      </div>
    </div>
  );
};

export default SmallModalEdit;
